import { capture } from '@client/utils/analytics';
import { takeBackgroundPreloadSource } from '@client/utils/route-preloader';
import { isRouteChunkWarm } from '@client/utils/routing/route-chunk-warmth';
import { hasClientNavigationStarted } from '@client/utils/scheduling/scheduleOnIdle';
import type { AnyRoute, AnyRouter } from '@tanstack/react-router';

type PendingNavigation = {
    href: string;
    startedAt: number;
    routeId: string;
    chunksWarm: boolean;
};

/**
 * Records, per client-side navigation, whether the destination's code was already in memory and
 * how long the navigation took, as the PostHog event `navigation_route_loaded`. This is the
 * measure of what the route preloaders (early, idle, hover intent, redirect) buy real users;
 * without it their thresholds and ordering are guesswork.
 *
 * Only navigations that change the path and follow the first render count: the initial
 * document load, and the auth callback's hand-off to the landing page, are always cold and
 * would swamp the hit rate. Register this after `trackIdleSchedulingNavigation`, which
 * maintains that signal on the same event. A navigation to a path no route owns is not
 * counted either; it renders the not-found page, not a route whose chunk could be warm.
 *
 * One navigation is pending at a time. A redirect chain (an index route's `beforeLoad`
 * redirecting to its first tab) starts a new `onBeforeLoad` before the first resolves, so the
 * event describes the final destination and times from the redirect.
 *
 * Only the matched route's pattern is captured (`/console/$orgId/claims/details/$id`), never
 * the pathname, search or any identifier.
 */
export function trackRouteChunkWarmth(router: AnyRouter, now: () => number = () => performance.now()): () => void {
    let pending: PendingNavigation | undefined;

    const unsubscribeBeforeLoad = router.subscribe('onBeforeLoad', ({ fromLocation, toLocation, pathChanged }) => {
        if (!fromLocation || !pathChanged || !hasClientNavigationStarted()) {
            return;
        }
        const routes = matchedRoutes(router, toLocation.pathname);
        const leaf = routes?.at(-1);
        if (!routes || !leaf) {
            return;
        }
        pending = {
            href: toLocation.href,
            startedAt: now(),
            routeId: leaf.id,
            chunksWarm: routes.filter((route) => route.options.component).every(isRouteChunkWarm),
        };
    });

    const unsubscribeResolved = router.subscribe('onResolved', ({ toLocation }) => {
        // The page's own chunk is the deepest one; layouts above it were loaded by the navigation
        // that reached this area, not by a preloader, so they do not decide the attribution. Only
        // a background preload (early, idle or redirect) that found the chunk cold counts: a
        // hover's preload, or one that found the chunk already loaded by an earlier visit, does not.
        // Whatever resolves at the page, measured or not (the initial document, and a redirect
        // warm-up's destination reached on it), takes that credit, so a later visit, which finds
        // the chunk loaded by this one, is not credited too. Taken on resolution, so a navigation
        // superseded before then leaves the credit for the one that lands.
        const page = matchedRoutes(router, toLocation.pathname)?.findLast((route) => route.options.component);
        const backgroundSource = page === undefined ? undefined : takeBackgroundPreloadSource(page.id);
        if (!pending || pending.href !== toLocation.href) {
            return;
        }
        const navigation = pending;
        pending = undefined;
        const appPreloadSource = navigation.chunksWarm ? (backgroundSource ?? null) : null;
        capture('navigation_route_loaded', {
            route_id: navigation.routeId,
            chunks_warm: navigation.chunksWarm,
            preloaded_by_app: appPreloadSource !== null,
            app_preload_source: appPreloadSource,
            duration_ms: Math.round(now() - navigation.startedAt),
        });
    });

    return () => {
        unsubscribeBeforeLoad();
        unsubscribeResolved();
    };
}

/**
 * The routes a pathname matches, root first, or nothing when no route owns it or the table
 * cannot answer. The router matches fuzzily: an unknown path still returns the deepest branch
 * on the way to it, with the rest of the path in the `**` param. That is how the router itself
 * decides to render not-found (`router-core`, `matchRoutesInternal`), so the same test applies.
 */
function matchedRoutes(router: AnyRouter, pathname: string): readonly AnyRoute[] | undefined {
    try {
        const [routes, rawParams, foundRoute] = router.getMatchedRoutes(pathname);
        if (!foundRoute || (foundRoute.path !== '/' && rawParams['**'] !== undefined)) {
            return undefined;
        }
        return routes;
    } catch {
        // Telemetry must never interfere with routing.
        return undefined;
    }
}
